import download from "downloadjs";
import t from "../../i18n";
import type { Translation } from "../../api/useTranslations";
import { generateExportFileName } from "diagnostics-lib/string";
import type { PerformanceReporting, DiagnosisReporting } from "../../api/useReporting";

const PERFORMANCE_HEADERS = [
  "date",
  "project name",
  "project site",
  "project type",
  "qoi",
  "expected",
  "measured",
];
export function exportPerformanceReporting(reporting: PerformanceReporting) {
  const data = reporting.periods.flatMap(dateReporting =>
    dateReporting.values.map(value => {
      const project = reporting.projectById[value.projectId];
      const qoiName = reporting.qoiById[value.qoiId]?.name;

      if (project === undefined || qoiName === undefined) throw new Error("Invalid data");
      return [
        new Date(dateReporting.period).toISOString(),
        project?.name ?? "",
        project?.siteId ?? "",
        project?.typeId ?? "",
        qoiName,
        value.expected,
        value.value,
      ];
    }),
  );

  const dataToExport = [PERFORMANCE_HEADERS, ...data];
  const fileName = generateExportFileName({ filename: "performance", extension: "csv" });
  const csv = dataToExport.map(line => line.join(";")).join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  download(blob, fileName);
}

const DIAGNOSIS_HEADERS = [
  "date",
  "project name",
  "project site",
  "project type",
  "failure",
  "category",
  "system",
  "reparability",
  "degradation type",
  "equipment type",
  "qoi",
  "impact",
];
export function exportDiagnosisReporting(reporting: DiagnosisReporting, translation: Translation) {
  const data = reporting.periods.flatMap(dateReporting =>
    dateReporting.values.flatMap(value => {
      if (value.impacts.length === 0) return [];
      return value.impacts.map(impact => {
        const project = reporting.projectById[value.projectId];
        const qoiName = reporting.qoiById[value.qoiId]?.name;
        const failure = reporting.failureById[impact.failureId];

        if (project === undefined || qoiName === undefined || failure === undefined)
          throw new Error("Invalid data");

        return [
          new Date(dateReporting.period).toISOString(),
          project?.name ?? "",
          project?.siteId ?? "",
          project?.typeId ?? "",
          failure.displayName,
          failure.failureCategoryId ?? "",
          failure.systemId ?? "",
          impact.reparability
            ? t("reparability", impact.reparability, translation)
            : t("reparability.none"),
          failure.degradationTypeId ?? "",
          failure.equipmentTypeId ?? "",
          qoiName,
          impact.value,
        ];
      });
    }),
  );

  const dataToExport = [DIAGNOSIS_HEADERS, ...data];
  const fileName = generateExportFileName({ filename: "diagnosis", extension: "csv" });
  const csv = dataToExport.map(line => line.join(";")).join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  download(blob, fileName);
}
